import { normalizeSiteLanguage, type SiteLanguage } from './routing';
import { withAttribution } from './googleRegistrationAttribution';
import type { PartnerKind } from '../../shared/partnerTerms';

/**
 * The real partner programme lives on `partner.bidmio.{sk,cz,dk,com}` (served by
 * the Bidmio app). Sign-up there is self-service; this site only links into it.
 * Germany has no partner origin of its own, so German visitors use `.com` with
 * `lang=de` (the portal speaks all five site languages).
 */
const PARTNER_TLD_BY_LANG: Record<SiteLanguage, string> = {
  sk: 'sk',
  cs: 'cz',
  da: 'dk',
  en: 'com',
  de: 'com',
};

/** Portal paths this site may link to; anything else is a programming error. */
export type PartnerPortalPath = '/join' | '/login' | '/program-terms' | '/privacy';

export type { PartnerKind };

/** Which join button was clicked; becomes `utm_campaign=partneri-<placement>`. */
export type PartnerPlacement = 'hero' | 'creator' | 'marketer' | 'accountant';

/** The partner kinds the page offers, as the portal sign-up names them. */
export const PARTNER_KIND_TABS: ReadonlyArray<{ kind: PartnerKind; placement: PartnerPlacement }> = [
  { kind: 'creator', placement: 'creator' },
  { kind: 'marketer', placement: 'marketer' },
  { kind: 'accounting_firm', placement: 'accountant' },
];

export const PARTNER_PORTAL_ORIGINS = [
  'https://partner.bidmio.sk',
  'https://partner.bidmio.cz',
  'https://partner.bidmio.dk',
  'https://partner.bidmio.com',
] as const;

/** `utm_source` for each site; only `[\w.+~-]` so the portal keeps the tag. */
const SITE_HOST_BY_LANG: Record<SiteLanguage, string> = {
  sk: 'bidmio.sk',
  cs: 'bidmio.cz',
  da: 'bidmio.dk',
  en: 'bidmio.com',
  de: 'bidmio.de',
};

export function partnerPortalOrigin(lang?: string | null): string {
  return `https://partner.bidmio.${PARTNER_TLD_BY_LANG[normalizeSiteLanguage(lang)]}`;
}

/** The portal host without scheme, e.g. `partner.bidmio.sk`, for display. */
export function partnerPortalHost(lang?: string | null): string {
  return new URL(partnerPortalOrigin(lang)).host;
}

type PartnerUrlOptions = {
  kind?: PartnerKind;
  /** The visitor's campaign query (from CampaignContext); sanitised again here. */
  campaign?: string;
  /** Which join button was clicked, so the portal can tell placements apart. */
  placement?: PartnerPlacement;
};

/**
 * Build a portal link with a fixed host: no caller input can change the origin
 * or path. Join links carry the visitor's own campaign (and `?ref=`) plus this
 * site's UTM tags, which only fill gaps and never overwrite an ad click.
 */
export function partnerPortalUrl(
  lang: string | null | undefined,
  path: PartnerPortalPath,
  { kind, campaign = '', placement }: PartnerUrlOptions = {},
): string {
  const siteLang = normalizeSiteLanguage(lang);
  const url = new URL(`${partnerPortalOrigin(siteLang)}${path}`);
  let params = new URLSearchParams();
  if (path === '/join') {
    const campaignName = placement ? `partneri-${placement}` : 'partneri';
    const ours = `utm_source=${SITE_HOST_BY_LANG[siteLang]}&utm_medium=web&utm_campaign=${campaignName}`;
    params = new URLSearchParams(withAttribution(campaign, ours));
    if (kind) params.set('kind', kind);
  }
  params.set('lang', siteLang);
  url.search = params.toString();
  return url.toString();
}
